import { useState, useMemo, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/hooks/useAuthStore';
import {
  LogOut,
  Menu,
  X,
  ChevronDown,
  Settings,
} from 'lucide-react';
import AIAssistant from '../AIAssistant';

// Navigation items by account type — grouped into logical sections
type NavItem = { name: string; href: string };
type NavGroup = { label: string; items: NavItem[] };

const generatorNavigation: NavGroup[] = [
  {
    label: '',
    items: [{ name: 'Dashboard', href: '/' }],
  },
  {
    label: 'Setup',
    items: [
      { name: 'My Company', href: '/generators' },
      { name: 'Wastestreams', href: '/waste-profiles' },
      { name: 'Documents', href: '/documents' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'Pickups', href: '/dispatch' },
      { name: 'Manifests', href: '/manifests' },
    ],
  },
  {
    label: 'Account',
    items: [
      { name: 'Compliance', href: '/compliance' },
      { name: 'Billing', href: '/billing' },
    ],
  },
];

const brokerNavigation: NavGroup[] = [
  {
    label: '',
    items: [{ name: 'Dashboard', href: '/' }],
  },
  {
    label: 'Setup',
    items: [
      { name: 'Customers', href: '/customers' },
      { name: 'Generators', href: '/generators' },
      { name: 'Wastestreams', href: '/waste-profiles' },
      { name: 'Documents', href: '/documents' },
    ],
  },
  {
    label: 'Analyzers',
    items: [
      { name: 'SDS Analyzer', href: '/tools/sds-analyzer' },
      { name: 'Lab Report Analyzer', href: '/tools/lab-report-analyzer' },
      { name: 'Photo → Profile', href: '/tools/photo-to-profile' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'TPA Management', href: '/tpa' },
      { name: 'Pickups', href: '/dispatch' },
      { name: 'Manifests', href: '/manifests' },
      { name: 'e-Manifest', href: '/emanifest' },
      { name: 'Transportation', href: '/transportation' },
      { name: 'Disposal Sites', href: '/disposal-sites' },
    ],
  },
  {
    label: 'Account',
    items: [
      { name: 'Compliance', href: '/compliance' },
      { name: 'Billing', href: '/billing' },
    ],
  },
];

// Field tech = an operator on site (role=field_tech inside an admin/broker org).
// Trimmed, field-first nav: complete pickups, profile waste on site, manifests,
// and the on-site SDS tool. No billing/compliance/customers/admin management.
const fieldTechNavigation: NavGroup[] = [
  {
    label: '',
    items: [{ name: 'Field Dashboard', href: '/' }],
  },
  {
    label: 'On Site',
    items: [
      { name: 'My Jobs', href: '/field/jobs' },
      { name: 'Pickups', href: '/dispatch' },
      { name: 'New Wastestream', href: '/waste-profiles/wizard' },
      { name: 'Wastestreams', href: '/waste-profiles' },
      { name: 'Manifests', href: '/manifests' },
    ],
  },
  {
    label: 'Tools',
    items: [{ name: 'SDS Analyzer', href: '/tools/sds-analyzer' }],
  },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization, logout, getAccountType, isFieldTech } = useAuthStore();

  // Clean up stale test mode localStorage on mount
  useEffect(() => { localStorage.removeItem('hazwaste_test_view_mode'); }, []);

  const isBroker = getAccountType() === 'broker';
  const fieldTech = isFieldTech();

  // Get navigation based on role/account type (field tech overrides account type)
  const navigation = useMemo(() => {
    if (fieldTech) return fieldTechNavigation;
    return isBroker ? brokerNavigation : generatorNavigation;
  }, [fieldTech, isBroker]);

  // Label for display
  const accountTypeLabel = fieldTech ? 'Field Tech' : isBroker ? 'Broker' : 'Generator';

  // Unified light theme (HazEasy_Design_System.md §3 + §4)
  // Sidebar + topbar: white/warm neutral, brand teal as single accent
  const theme = {
    sidebar: 'bg-white',
    sidebarBorder: 'border-neutral-200',
    logo: 'text-neutral-900',
    navLink: 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
    navActive: 'bg-brand-50 text-brand-700 font-semibold',
    footerLink: 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
    footerIcon: 'text-neutral-400',
    header:
      'bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-neutral-200',
    headerBtn: 'hover:bg-neutral-100 text-neutral-600 hover:text-neutral-900',
    avatar: 'bg-brand-100 text-brand-700',
    userName: 'text-neutral-900',
    userOrg: 'text-neutral-500',
    chevron: 'text-neutral-400',
    menuBg: 'bg-white border-neutral-200',
    menuText: 'text-neutral-900',
    menuSub: 'text-neutral-500',
    menuItem: 'hover:bg-neutral-50',
    menuDivider: 'border-neutral-200',
    groupLabel: 'text-neutral-400',
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
      >
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
        <div className={`fixed inset-y-0 left-0 w-64 ${theme.sidebar} shadow-xl`}>
          <div className={`flex items-center justify-between h-14 px-4 border-b ${theme.sidebarBorder}`}>
            <span className={`text-lg font-semibold ${theme.logo}`}>HazEasy</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`p-2 rounded-md ${theme.headerBtn}`}
              aria-label="Close sidebar"
            >
              <X className="w-7 h-7" />
            </button>
          </div>
          <nav className="p-2 space-y-3 overflow-y-auto">
            {navigation.map((group) => (
              <div key={group.label || 'top'}>
                {group.label && (
                  <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${theme.groupLabel}`}>
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`block px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 ${
                          isActive ? theme.navActive : theme.navLink
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col">
        <div className={`flex flex-col flex-1 ${theme.sidebar} border-r ${theme.sidebarBorder}`}>
          <div className={`flex items-center h-14 px-4 border-b ${theme.sidebarBorder}`}>
            <span className={`text-lg font-semibold ${theme.logo}`}>HazEasy</span>
          </div>
          <nav className="flex-1 px-2 py-3 space-y-3 overflow-y-auto">
            {navigation.map((group) => (
              <div key={group.label || 'top'}>
                {group.label && (
                  <p className={`px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider ${theme.groupLabel}`}>
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          isActive ? theme.navActive : theme.navLink
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          <div className={`px-2 py-2 border-t ${theme.sidebarBorder} space-y-0.5`}>
            <a
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              className={`block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${theme.footerLink}`}
            >
              Help
            </a>
            <Link
              to="/settings"
              className={`block px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${theme.footerLink}`}
            >
              Settings
            </Link>
            {/* Build commit hash — quick visual confirmation that the
                deployed bundle includes the latest push. Muted footer
                text. Title shows full hash + build time on hover. */}
            <div
              className="block px-3 py-1.5 text-[10px] font-mono text-neutral-400 select-text"
              title={`Frontend build ${__BUILD_COMMIT__} (${__BUILD_TIME__})`}
            >
              build {__BUILD_COMMIT_SHORT__}
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-56">
        {/* Top header */}
        <header className={`sticky top-0 z-40 flex items-center h-12 px-4 border-b sm:px-6 lg:px-8 ${theme.header}`}>
          <button
            onClick={() => setSidebarOpen(true)}
            className={`p-2 -ml-2 rounded-md lg:hidden ${theme.headerBtn}`}
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-xl transition-colors min-h-[44px] ${theme.headerBtn}`}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
            >
              <div className={`w-8 h-8 rounded-full ${theme.avatar} flex items-center justify-center`}>
                <span className="text-sm font-semibold">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <div className={`font-medium ${theme.userName}`}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div className={`text-xs ${theme.userOrg}`}>{organization?.name}</div>
              </div>
              <ChevronDown className={`w-4 h-4 ${theme.chevron}`} />
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg border py-1 z-50 ${theme.menuBg}`}>
                  <div className={`px-4 py-3 border-b ${theme.menuDivider}`}>
                    <div className={`text-sm font-medium ${theme.menuText}`}>
                      {user?.email}
                    </div>
                    <div className={`text-xs mt-1 ${theme.menuSub}`}>
                      {accountTypeLabel} | {organization?.subscriptionTier} Plan
                    </div>
                  </div>
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${theme.menuText} ${theme.menuItem}`}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <div className={`border-t my-1 ${theme.menuDivider}`} />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-600 hover:bg-danger-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-5 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* AI Regulatory Assistant */}
      <AIAssistant />
    </div>
  );
}
